/**
 * Data Fetch Strategy Context
 * Network-aware data fetching configuration based on connection quality
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useNetworkMonitor, ConnectionQuality } from '@/features/network/useNetworkMonitor';
import { analytics } from '@/services/analytics';
import { networkLogger } from '@/shared/lib/logger';

// Storage key for data saver preference
const DATA_SAVER_KEY = 'agxl_data_saver_mode';
const DATA_USAGE_KEY = 'agxl_data_usage_stats';

/**
 * Image quality levels for network conditions
 */
export type ImageQualityLevel = 'full' | 'medium' | 'thumbnail' | 'micro';

/**
 * Data fetch strategy configuration
 */
export interface DataFetchStrategy {
  /** Whether to perform fetches at all */
  shouldFetch: boolean;
  
  /** Whether to prefetch additional data */
  shouldPrefetch: boolean;
  
  /** Current image quality level */
  imageQuality: ImageQualityLevel;
  
  /** Whether data saver mode is enabled (manual override) */
  isDataSaverEnabled: boolean;
  
  /** Connection quality from network monitor */
  connectionQuality: ConnectionQuality;
  
  /** Whether device is in save-data mode */
  isSaveDataMode: boolean;
  
  /** Get page size for paginated queries */
  getPageSize: (defaultSize?: number) => number;
  
  /** Get activities page size */
  getActivitiesPageSize: () => number;
  
  /** Get photos page size */
  getPhotosPageSize: () => number;
  
  /** Get network feed page size */
  getNetworkFeedPageSize: () => number;
  
  /** Whether to load full images or thumbnails only */
  loadFullImages: boolean;
  
  /** Whether to auto-play media */
  autoPlayMedia: boolean;
  
  /** Maximum file size to auto-download (in bytes) */
  maxAutoDownloadSize: number;
  
  /** Toggle data saver mode */
  toggleDataSaver: () => void;
  
  /** Set data saver mode */
  setDataSaverEnabled: (enabled: boolean) => void;
  
  /** Estimated data savings percentage */
  estimatedSavings: number;
  
  /** Track data usage */
  trackDataUsage: (bytes: number, type: 'api' | 'image' | 'media') => void;
  
  /** Get data usage statistics */
  getDataUsageStats: () => DataUsageStats;
  
  /** Reset data usage statistics */
  resetDataUsageStats: () => void;
}

/**
 * Data usage statistics
 */
export interface DataUsageStats {
  totalBytes: number;
  apiBytes: number;
  imageBytes: number;
  mediaBytes: number;
  savedBytes: number;
  sessionStart: number;
}

/**
 * Default strategy for SSR/initial state
 */
const defaultStrategy: DataFetchStrategy = {
  shouldFetch: true,
  shouldPrefetch: true,
  imageQuality: 'full',
  isDataSaverEnabled: false,
  connectionQuality: 'moderate',
  isSaveDataMode: false,
  getPageSize: (defaultSize = 20) => defaultSize,
  getActivitiesPageSize: () => 20,
  getPhotosPageSize: () => 20,
  getNetworkFeedPageSize: () => 10,
  loadFullImages: true,
  autoPlayMedia: true,
  maxAutoDownloadSize: 5 * 1024 * 1024, // 5MB
  toggleDataSaver: () => {},
  setDataSaverEnabled: () => {},
  estimatedSavings: 0,
  trackDataUsage: () => {},
  getDataUsageStats: () => ({
    totalBytes: 0,
    apiBytes: 0,
    imageBytes: 0,
    mediaBytes: 0,
    savedBytes: 0,
    sessionStart: Date.now(),
  }),
  resetDataUsageStats: () => {},
};

const DataFetchStrategyContext = createContext<DataFetchStrategy>(defaultStrategy);

/**
 * Page size configurations by connection quality
 */
const PAGE_SIZE_CONFIG: Record<ConnectionQuality, { activities: number; photos: number; feed: number }> = {
  fast: { activities: 25, photos: 24, feed: 15 },
  moderate: { activities: 15, photos: 12, feed: 10 },
  slow: { activities: 10, photos: 6, feed: 5 },
  offline: { activities: 0, photos: 0, feed: 0 },
};

/**
 * Image quality by connection
 */
const IMAGE_QUALITY_CONFIG: Record<ConnectionQuality, ImageQualityLevel> = {
  fast: 'full',
  moderate: 'medium',
  slow: 'thumbnail',
  offline: 'micro',
};

/**
 * Max auto-download size by connection (bytes)
 */
const MAX_DOWNLOAD_CONFIG: Record<ConnectionQuality, number> = {
  fast: 10 * 1024 * 1024,    // 10MB
  moderate: 5 * 1024 * 1024,  // 5MB
  slow: 1 * 1024 * 1024,      // 1MB
  offline: 0,
};

/**
 * Estimated savings percentages by mode
 */
const SAVINGS_ESTIMATES = {
  normal: 0,
  moderate: 35,
  slow: 60,
  dataSaver: 70,
};

interface DataFetchStrategyProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for data fetch strategy
 */
export const DataFetchStrategyProvider: React.FC<DataFetchStrategyProviderProps> = ({ children }) => {
  const { quality, status, isOnline } = useNetworkMonitor();
  
  // Data saver mode (manual override)
  const [isDataSaverEnabled, setIsDataSaverEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(DATA_SAVER_KEY) === 'true';
  });
  
  // Data usage tracking
  const [dataUsage, setDataUsage] = useState<DataUsageStats>(() => {
    if (typeof window === 'undefined') {
      return {
        totalBytes: 0,
        apiBytes: 0,
        imageBytes: 0,
        mediaBytes: 0,
        savedBytes: 0,
        sessionStart: Date.now(),
      };
    }
    
    try {
      const stored = localStorage.getItem(DATA_USAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Reset if older than 24 hours
        if (Date.now() - parsed.sessionStart > 24 * 60 * 60 * 1000) {
          return {
            totalBytes: 0,
            apiBytes: 0,
            imageBytes: 0,
            mediaBytes: 0,
            savedBytes: 0,
            sessionStart: Date.now(),
          };
        }
        return parsed;
      }
    } catch (e) {
      // Ignore parse errors
    }
    
    return {
      totalBytes: 0,
      apiBytes: 0,
      imageBytes: 0,
      mediaBytes: 0,
      savedBytes: 0,
      sessionStart: Date.now(),
    };
  });

  // Persist data saver preference
  useEffect(() => {
    localStorage.setItem(DATA_SAVER_KEY, String(isDataSaverEnabled));
    
    if (isDataSaverEnabled) {
      networkLogger.info('Data Saver mode enabled');
      analytics.featureUsage('Data Saver Enabled');
    }
  }, [isDataSaverEnabled]);

  // Persist data usage stats
  useEffect(() => {
    localStorage.setItem(DATA_USAGE_KEY, JSON.stringify(dataUsage));
  }, [dataUsage]);

  // Effective quality (data saver forces slow behavior)
  const effectiveQuality = useMemo((): ConnectionQuality => {
    if (isDataSaverEnabled) return 'slow';
    if (status.saveData) return 'slow';
    return quality;
  }, [isDataSaverEnabled, status.saveData, quality]);

  // Toggle data saver
  const toggleDataSaver = useCallback(() => {
    setIsDataSaverEnabled((prev) => !prev);
    if ('vibrate' in navigator) {
      navigator.vibrate(15);
    }
  }, []);

  // Track data usage
  const trackDataUsage = useCallback((bytes: number, type: 'api' | 'image' | 'media') => {
    setDataUsage((prev) => {
      const newStats = { ...prev, totalBytes: prev.totalBytes + bytes };
      
      switch (type) {
        case 'api':
          newStats.apiBytes = prev.apiBytes + bytes;
          break;
        case 'image':
          newStats.imageBytes = prev.imageBytes + bytes;
          break;
        case 'media':
          newStats.mediaBytes = prev.mediaBytes + bytes;
          break;
      }
      
      return newStats;
    });
    
    // Track in analytics periodically (every 1MB)
    if (dataUsage.totalBytes > 0 && dataUsage.totalBytes % (1024 * 1024) < bytes) {
      analytics.featureUsage(`Data Usage: ${Math.round(dataUsage.totalBytes / 1024 / 1024)}MB`);
    }
  }, [dataUsage.totalBytes]);

  // Get data usage stats
  const getDataUsageStats = useCallback(() => dataUsage, [dataUsage]);

  // Reset data usage
  const resetDataUsageStats = useCallback(() => {
    setDataUsage({
      totalBytes: 0,
      apiBytes: 0,
      imageBytes: 0,
      mediaBytes: 0,
      savedBytes: 0,
      sessionStart: Date.now(),
    });
  }, []);

  // Build strategy object
  const strategy = useMemo((): DataFetchStrategy => {
    const pageSizes = PAGE_SIZE_CONFIG[effectiveQuality];
    
    return {
      shouldFetch: isOnline && effectiveQuality !== 'offline',
      shouldPrefetch: effectiveQuality === 'fast' && !isDataSaverEnabled,
      imageQuality: IMAGE_QUALITY_CONFIG[effectiveQuality],
      isDataSaverEnabled,
      connectionQuality: effectiveQuality,
      isSaveDataMode: status.saveData || false,
      
      getPageSize: (defaultSize = 20) => {
        const multiplier = effectiveQuality === 'fast' ? 1.25 : 
                          effectiveQuality === 'moderate' ? 0.75 : 0.5;
        return Math.round(defaultSize * multiplier);
      },
      
      getActivitiesPageSize: () => pageSizes.activities,
      getPhotosPageSize: () => pageSizes.photos,
      getNetworkFeedPageSize: () => pageSizes.feed,
      
      loadFullImages: effectiveQuality === 'fast' && !isDataSaverEnabled,
      autoPlayMedia: effectiveQuality === 'fast' && !isDataSaverEnabled,
      maxAutoDownloadSize: MAX_DOWNLOAD_CONFIG[effectiveQuality],
      
      toggleDataSaver,
      setDataSaverEnabled: setIsDataSaverEnabled,
      
      estimatedSavings: isDataSaverEnabled 
        ? SAVINGS_ESTIMATES.dataSaver 
        : SAVINGS_ESTIMATES[effectiveQuality === 'moderate' ? 'moderate' : 
                           effectiveQuality === 'slow' ? 'slow' : 'normal'],
      
      trackDataUsage,
      getDataUsageStats,
      resetDataUsageStats,
    };
  }, [
    effectiveQuality, 
    isOnline, 
    isDataSaverEnabled, 
    status.saveData, 
    toggleDataSaver, 
    trackDataUsage,
    getDataUsageStats,
    resetDataUsageStats,
  ]);

  return (
    <DataFetchStrategyContext.Provider value={strategy}>
      {children}
    </DataFetchStrategyContext.Provider>
  );
};

/**
 * Hook to access data fetch strategy
 */
export const useDataFetchStrategy = (): DataFetchStrategy => {
  return useContext(DataFetchStrategyContext);
};

/**
 * Format bytes for display
 */
export const formatDataSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};